import React, { useState, useEffect, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X as XIcon } from "lucide-react";
import { toast } from "sonner";

const LN_ADDRESS = "xaelserpent@coinos.io";
// LNURL-pay endpoint for a Lightning Address (LUD-16 / LUD-06)
const LNURL_ENDPOINT = `https://coinos.io/.well-known/lnurlp/xaelserpent`;

function bech32Encode(prefix, data) {
  // We avoid bundling a bech32 lib; the human-readable LN address is enough for QR.
  return null;
}

export default function LightningTipModal({ open, onClose, suggestedSats = 1000 }) {
  const [amount, setAmount] = useState(suggestedSats);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paid, setPaid] = useState(false);
  const [weblnTried, setWeblnTried] = useState(false);
  const verifyTimerRef = useRef(null);
  const verifyAbortRef = useRef(false);

  // Stop polling on close / unmount
  useEffect(() => {
    if (!open) {
      setInvoice(null); setError(""); setPaid(false); setWeblnTried(false);
      verifyAbortRef.current = true;
      if (verifyTimerRef.current) { clearTimeout(verifyTimerRef.current); verifyTimerRef.current = null; }
    }
  }, [open]);

  useEffect(() => () => {
    verifyAbortRef.current = true;
    if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);
  }, []);

  if (!open) return null;

  const onPaymentSettled = (sats) => {
    setPaid(true);
    try {
      toast.success(`<img src="/bolt.png" alt="" className="inline-block h-4 w-4 align-middle" /> ${sats.toLocaleString()} sats received — thank you!`, {
        duration: 6000,
        style: { background: "#000", color: "#ffde16", border: "1px solid #ffde16" },
      });
    } catch { /* sonner not mounted — silent */ }
  };

  // LUD-21: poll invoice's `verify` URL until settled === true.
  const startVerifyPolling = (verifyUrl, sats) => {
    verifyAbortRef.current = false;
    const startedAt = Date.now();
    const MAX_MS = 15 * 60 * 1000; // 15 minutes
    const tick = async () => {
      if (verifyAbortRef.current) return;
      if (Date.now() - startedAt > MAX_MS) return;
      try {
        const r = await fetch(verifyUrl, { mode: "cors" });
        if (r.ok) {
          const j = await r.json();
          if (j && j.settled === true) {
            onPaymentSettled(sats);
            return;
          }
        }
      } catch { /* ignore network blips */ }
      verifyTimerRef.current = setTimeout(tick, 3000);
    };
    tick();
  };

  const fetchInvoice = async () => {
    setLoading(true); setError(""); setInvoice(null); setPaid(false);
    try {
      const lp = await fetch(LNURL_ENDPOINT, { mode: "cors" }).then((r) => r.json());
      if (!lp || !lp.callback) throw new Error("No callback from LNURLp endpoint");
      const msats = Math.max(lp.minSendable || 1000, Math.min(lp.maxSendable || 100000000000, amount * 1000));
      const cb = `${lp.callback}${lp.callback.includes("?") ? "&" : "?"}amount=${msats}`;
      const inv = await fetch(cb, { mode: "cors" }).then((r) => r.json());
      if (!inv || !inv.pr) throw new Error(inv?.reason || "no invoice returned");
      setInvoice(inv.pr);
      // LUD-21 verify URL — kicks off polling so we can fire a toast on settlement
      if (inv.verify) {
        startVerifyPolling(inv.verify, amount);
      }
      // Try WebLN if available
      if (typeof window !== "undefined" && window.webln && !weblnTried) {
        setWeblnTried(true);
        try {
          await window.webln.enable();
          await window.webln.sendPayment(inv.pr);
          onPaymentSettled(amount);
        } catch {
          // user declined or no extension — fall through to QR
        }
      }
    } catch (e) {
      setError(e?.message || "could not fetch invoice. you can still send to " + LN_ADDRESS);
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    if (invoice) navigator.clipboard?.writeText(invoice);
  };
  const copyLn = () => navigator.clipboard?.writeText(LN_ADDRESS);

  return (
    <div className="xs-modal-backdrop" onClick={onClose} data-testid="ln-modal-backdrop">
      <div
        className="xs-modal"
        style={{ maxWidth: 460 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="ln-modal"
      >
        <div className="xs-modal-header">
          <span><img src="/bolt.png" alt="" className="inline-block h-4 w-4 align-middle" /> tip in sats</span>
          <button className="xs-modal-close" onClick={onClose} aria-label="Close" data-testid="ln-modal-close">
            <XIcon size={14} />
          </button>
        </div>
        <div className="xs-modal-body" style={{ textAlign: "center" }}>
          {!invoice && !paid && (
            <>
              <p style={{ marginTop: 0 }}>
                send a tip directly over the lightning network. zero redirect, zero fees on small payments.
              </p>
              <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap", margin: "10px 0" }}>
                {[500, 1000, 5000, 21000].map((v) => (
                  <button
                    key={v}
                    className={`xs-btn ${amount === v ? "primary" : ""}`}
                    onClick={() => setAmount(v)}
                    data-testid={`ln-preset-${v}`}
                  >
                    {v.toLocaleString()} sats
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={100}
                step={100}
                value={amount}
                onChange={(e) => setAmount(Math.max(100, Number(e.target.value) || 0))}
                className="xs-input"
                style={{ maxWidth: 220, margin: "8px auto" }}
                data-testid="ln-amount-input"
              />
              <div>
                <button
                  className="xs-btn primary"
                  onClick={fetchInvoice}
                  disabled={loading}
                  data-testid="ln-generate-invoice"
                  style={{ marginTop: 10, minWidth: 200 }}
                >
                  {loading ? "fetching invoice…" : `generate ${amount.toLocaleString()} sats invoice`}
                </button>
              </div>
              <div style={{ marginTop: 12, fontSize: "0.8em", opacity: 0.75 }}>
                or send to <code style={{ color: "#ffde16" }} onClick={copyLn}>{LN_ADDRESS}</code>{" "}
                <button className="xs-btn ghost" style={{ padding: "4px 8px", minHeight: 0 }} onClick={copyLn}>copy</button>
              </div>
            </>
          )}

          {invoice && !paid && (
            <>
              <p style={{ margin: "0 0 8px" }}><strong>{amount.toLocaleString()} sats</strong> — scan or copy</p>
              <div style={{ background: "#fff", padding: 12, borderRadius: 8, display: "inline-block" }}>
                <QRCodeSVG value={`lightning:${invoice}`} size={220} bgColor="#ffffff" fgColor="#000000" level="M" />
              </div>
              <div style={{ marginTop: 10 }}>
                <a
                  href={`lightning:${invoice}`}
                  className="xs-btn primary"
                  style={{ marginRight: 8 }}
                  data-testid="ln-open-wallet"
                >
                  open in wallet
                </a>
                <button className="xs-btn" onClick={copy} data-testid="ln-copy-invoice">copy invoice</button>
              </div>
              <div style={{ fontSize: "0.7em", opacity: 0.6, marginTop: 8, wordBreak: "break-all" }}>
                {invoice.slice(0, 80)}…
              </div>
            </>
          )}

          {paid && (
            <div style={{ padding: 24, fontSize: "1.2em", color: "#6bff8a" }} data-testid="ln-paid">
              <img src="/bolt.png" alt="" className="inline-block h-4 w-4 align-middle" /> thank you — payment received!
            </div>
          )}

          {error && (
            <div style={{ color: "#ff9b6b", fontSize: "0.85em", marginTop: 12, lineHeight: 1.5 }} data-testid="ln-error">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
